import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import EmailThread from "../../leadsComponents/EmailThread";
import ConnectGmailButton from "../../leadsComponents/ConnectGmailButton";
import { INQUIRY_TYPES, TYPE_TONE } from "../constants";
import {
  classNames,
  firstResponseMinutes,
  formatDateTime,
  formatDuration,
  formatRelative,
  statusOf,
  urgencyFor,
  urgencyTone,
} from "./utils";
import InquiryReplyComposer from "./InquiryReplyComposer";

// Modal replacement for InquiryDetailDrawer. Keeps every capability of the
// drawer (status toggles, notes, thread, composer) but in a centered, larger,
// more scannable surface with a left summary column + right reply column.
export default function InquiryReplyModal({
  open,
  inquiry,
  loading,
  adminInfo,
  gmailConnected,
  onConnectGmail,
  onClose,
  onPatchInquiry,
  onSendReply,
  onNext,
  onPrevious,
  onManageTemplates,
}) {
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    setNotesDraft(inquiry?.notes || "");
    setSendError("");
  }, [inquiry?._id, inquiry?.notes]);

  const responderName = `${adminInfo?.firstName || ""} ${adminInfo?.lastName || ""}`.trim();
  const tone = TYPE_TONE[inquiry?.type || "Other"] || TYPE_TONE.Other;
  const firstRespMin = useMemo(() => firstResponseMinutes(inquiry), [inquiry]);

  const saveNotes = async () => {
    if (!inquiry?._id) return;
    setSavingNotes(true);
    try {
      await onPatchInquiry?.(inquiry._id, { notes: notesDraft });
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleClosed = async () => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { closed: !inquiry.closed });
  };

  const markReplied = async () => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { replied: true });
  };

  const changeType = async (next) => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { type: next });
  };

  const handleSendReply = async (payload) => {
    if (!inquiry?._id) return;
    setSending(true);
    setSendError("");
    try {
      await onSendReply?.({
        id: inquiry._id,
        responder: responderName,
        ...payload,
      });
      // Optimistic close: the slice extraReducer already flipped `replied`
      // on sendInquiryEmail.fulfilled, so the dashboard list updates
      // immediately. Closing the modal here keeps the interaction snappy.
      onClose?.();
    } catch (err) {
      setSendError(err?.response?.data?.error || err?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={() => onClose?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-stretch justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                {!inquiry ? (
                  <div className="flex h-[60vh] items-center justify-center text-sm text-zinc-500">
                    {loading ? "Loading inquiry…" : "No inquiry selected"}
                  </div>
                ) : (
                  <>
                    <Header
                      inquiry={inquiry}
                      firstRespMin={firstRespMin}
                      onNext={onNext}
                      onPrevious={onPrevious}
                      onClose={onClose}
                    />

                    <StatusBar
                      inquiry={inquiry}
                      tone={tone}
                      onChangeType={changeType}
                      onMarkReplied={markReplied}
                      onToggleClosed={toggleClosed}
                    />

                    <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
                      <aside className="flex min-h-0 flex-col overflow-y-auto border-b border-zinc-200 bg-zinc-50/60 lg:border-b-0 lg:border-r">
                        <div className="space-y-3 p-4">
                          <Card
                            title="Original message"
                            action={
                              inquiry.subject ? (
                                <span className="truncate text-[11px] font-medium text-zinc-700">
                                  {inquiry.subject}
                                </span>
                              ) : null
                            }
                          >
                            <div className="text-[11px] text-zinc-500">
                              Received {formatDateTime(inquiry.createdAt)}
                            </div>
                            <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm text-zinc-800 ring-1 ring-zinc-200">
                              {inquiry.message || (
                                <span className="italic text-zinc-400">(no message body)</span>
                              )}
                            </div>
                          </Card>

                          {inquiry.threadId || (inquiry.messages || []).length ? (
                            <Card title="Gmail thread">
                              <EmailThread
                                messages={inquiry.messages || []}
                                inquiryId={inquiry._id}
                                threadId={inquiry.threadId}
                                key={inquiry.threadId || inquiry._id}
                              />
                            </Card>
                          ) : null}

                          <Card
                            title="Internal notes"
                            action={
                              <button
                                type="button"
                                onClick={saveNotes}
                                disabled={savingNotes}
                                className="rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-500 disabled:bg-zinc-300"
                              >
                                {savingNotes ? "Saving…" : "Save"}
                              </button>
                            }
                          >
                            <textarea
                              rows={3}
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              placeholder="Private context for your team"
                              className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            />
                          </Card>

                          <Card title={`Response history (${(inquiry.responses || []).length})`}>
                            {(inquiry.responses || []).length === 0 ? (
                              <p className="text-xs text-zinc-500">No replies logged yet.</p>
                            ) : (
                              <ul className="space-y-2">
                                {inquiry.responses
                                  .slice()
                                  .reverse()
                                  .map((resp, idx) => (
                                    <li
                                      key={`${resp.respondedAt || idx}-${idx}`}
                                      className="rounded-lg border border-zinc-200 bg-white p-2.5"
                                    >
                                      <div className="flex items-center justify-between text-xs">
                                        <span className="font-semibold text-zinc-800">
                                          {resp.responder || "Unknown"}
                                        </span>
                                        <span className="text-zinc-500">
                                          {resp.respondedAt
                                            ? format(new Date(resp.respondedAt), "PPpp")
                                            : "—"}
                                        </span>
                                      </div>
                                      <div className="mt-1 whitespace-pre-wrap text-sm text-zinc-700">
                                        {resp.message}
                                      </div>
                                      {resp.templateUsed ? (
                                        <div className="mt-1 text-[11px] text-zinc-400">
                                          Template: {resp.templateUsed}
                                        </div>
                                      ) : null}
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </Card>
                        </div>
                      </aside>

                      <section className="flex min-h-0 flex-col overflow-y-auto">
                        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
                          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Reply
                          </h3>
                          {onManageTemplates ? (
                            <button
                              type="button"
                              onClick={onManageTemplates}
                              className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
                            >
                              Manage templates
                            </button>
                          ) : null}
                        </div>
                        <div className="flex-1 p-4">
                          {gmailConnected ? (
                            <InquiryReplyComposer
                              inquiry={inquiry}
                              adminInfo={adminInfo}
                              sending={sending}
                              sendError={sendError}
                              onSend={handleSendReply}
                            />
                          ) : (
                            <div className="space-y-2">
                              <p className="text-sm text-zinc-600">
                                Connect Gmail to send a reply from your mailbox.
                              </p>
                              {onConnectGmail ? (
                                <button
                                  onClick={onConnectGmail}
                                  className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                                >
                                  Connect Gmail
                                </button>
                              ) : (
                                <ConnectGmailButton />
                              )}
                            </div>
                          )}
                        </div>
                      </section>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function Card({ title, action, children }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </h4>
        {action}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function Header({ inquiry, firstRespMin, onNext, onPrevious, onClose }) {
  return (
    <header className="flex items-start justify-between gap-3 border-b border-zinc-200 px-4 py-3">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <Dialog.Title className="truncate text-base font-semibold text-zinc-900">
            {inquiry.name || "Unnamed"}
          </Dialog.Title>
          <StatusBadge inquiry={inquiry} />
          <UrgencyChip inquiry={inquiry} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
          <span className="truncate">{inquiry.email || "no email"}</span>
          {inquiry.phone ? (
            <>
              <span className="text-zinc-300">·</span>
              <span>{inquiry.phone}</span>
            </>
          ) : null}
          <span className="text-zinc-300">·</span>
          <span title={new Date(inquiry.createdAt).toString()}>
            received {formatRelative(inquiry.createdAt)}
          </span>
          {firstRespMin != null ? (
            <>
              <span className="text-zinc-300">·</span>
              <span>1st reply in {formatDuration(firstRespMin)}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrevious}
          title="Previous (k)"
          className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onNext}
          title="Next (j)"
          className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

function StatusBar({ inquiry, tone, onChangeType, onMarkReplied, onToggleClosed }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 bg-zinc-50/60 px-4 py-2">
      <div className="flex items-center gap-1.5">
        <span
          className={classNames(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
            tone.chip
          )}
        >
          <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
          {inquiry.type || "Other"}
        </span>
        <select
          value={inquiry.type || "Other"}
          onChange={(e) => onChangeType(e.target.value)}
          className="rounded-md border border-zinc-200 bg-white px-1.5 py-1 text-[11px] text-zinc-700 focus:border-indigo-500 focus:outline-none"
        >
          {INQUIRY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="ml-auto flex items-center gap-1">
        {!inquiry.replied && !inquiry.closed ? (
          <button
            type="button"
            onClick={onMarkReplied}
            className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
            title="Mark as replied without sending"
          >
            Mark replied
          </button>
        ) : null}
        <button
          type="button"
          onClick={onToggleClosed}
          className={classNames(
            "rounded-md border px-2 py-1 text-[11px] font-medium",
            inquiry.closed
              ? "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
              : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
          )}
        >
          {inquiry.closed ? "Reopen" : "Close inquiry"}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ inquiry }) {
  const status = statusOf(inquiry);
  const map = {
    new: { label: "New", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    replied: { label: "Replied", cls: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
    closed: { label: "Closed", cls: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  };
  const meta = map[status];
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        meta.cls
      )}
    >
      {meta.label}
    </span>
  );
}

function UrgencyChip({ inquiry }) {
  const id = urgencyFor(inquiry);
  if (!id) return null;
  const tone = urgencyTone(id);
  const label = { overdue: "Overdue", stale: "Stale", fresh: "Fresh", new: "Just now" }[id];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        tone.chip
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {label}
    </span>
  );
}
